import React, { CSSProperties } from 'react';

import { Field as FormikField, FieldValidator, FieldProps } from 'formik';

import withGrid from 'hocs/withGrid';
import useStoreTranslation from 'hooks/useStoreTranslation';

import { FieldLabel } from './styles';

export interface DefaultFieldComponentProps<T> {
  error?: string;
  onClick?: (e: React.SyntheticEvent) => void;
  onChange?: (e: React.ChangeEvent<T>) => void;
  onBlur?: (e: React.ChangeEvent<T>) => void;
}

interface Props {
  name: string;
  label?: string | JSX.Element;
  labelKey?: string;
  validate?: FieldValidator;
  children: JSX.Element;
  tooltip?: boolean;
  tooltipTarget?: string;
  tooltipClick?: (e: React.MouseEvent<HTMLElement>) => void;
  removeMask?: (value: any) => void;
  className?: string;
  style?: CSSProperties;
  simpleTooltip?: boolean;
  simpleTooltipText?: string;
}

const Field = ({
  name,
  label,
  labelKey,
  validate,
  children,
  removeMask,
  tooltip,
  tooltipClick,
  tooltipTarget,
  className,
  style,
  simpleTooltip,
  simpleTooltipText,
}: Props) => {
  const { t } = useStoreTranslation();

  return (
    <FormikField name={name} validate={validate}>
      {({ field, form }: FieldProps<string>) => {
        const { setFieldValue, setFieldTouched } = form;

        let error = form && form.errors[field.name] ? String(form.errors[field.name]) : undefined;

        if (error) {
          const splitted = error.split('/');
          if (splitted.length === 2) {
            error = t(splitted[0], { value: splitted[1] });
          } else {
            error = t(splitted[0]);
          }
        }

        const hasTouched = form ? Boolean(form.touched[field.name]) : false;

        const wrappers = {
          onChange: (e: any) => {
            if (removeMask) {
              e.target.value = removeMask(e.target.value);
            }
            if (children.props && children.props.onChange) {
              children.props.onChange(e);
            }
            if (field) field.onChange(e);
          },
          onBlur: (e: any) => {
            if (children.props && children.props.onBlur) {
              children.props.onBlur(e);
            }
            if (field) field.onBlur(e);
          },
        };

        return (
          <div className={className || ''} style={style}>
            {(label || labelKey) && (
              <FieldLabel
                styledProps={{ hasError: Boolean(error && hasTouched) }}
                messageKey={labelKey}
                content={label}
                htmlFor={name}
                tooltip={tooltip}
                tooltipClick={tooltipClick}
                tooltipClassName={tooltipTarget}
                simpleTooltip={simpleTooltip}
                simpleTooltipText={simpleTooltipText}
              />
            )}
            {React.cloneElement(children, {
              ...children.props,
              ...field,
              ...wrappers,
              error: error && hasTouched ? error : null,
              isValid: !error && hasTouched,
              rightCheckIfIsValid: form.status?.rightCheckIfIsValid,
              formik: {
                setFieldValue,
                setFieldTouched,
              },
            })}
          </div>
        );
      }}
    </FormikField>
  );
};

export default withGrid(Field);
